export type BoardSectionId = 'filters' | 'orders' | 'detail' | 'line';

export type BoardSectionsState = Record<BoardSectionId, boolean>;

/** True per section when it is collapsed to its header line. */
export const INITIAL_SECTIONS: BoardSectionsState = {
  filters: true,
  orders: false,
  detail: false,
  line: false,
};

export type BoardSectionsAction =
  | { type: 'toggle'; section: BoardSectionId }
  | { type: 'set'; section: BoardSectionId; collapsed: boolean }
  /** An order was opened: Orders folds up to its one-line summary. */
  | { type: 'orderSelected' }
  /** The detail was closed: Orders opens back up. */
  | { type: 'orderCleared' };

export function boardSectionsReducer(
  state: BoardSectionsState,
  action: BoardSectionsAction,
): BoardSectionsState {
  switch (action.type) {
    case 'toggle':
      return { ...state, [action.section]: !state[action.section] };
    case 'set':
      return state[action.section] === action.collapsed
        ? state
        : { ...state, [action.section]: action.collapsed };
    case 'orderSelected':
      return state.orders && !state.detail
        ? state
        : { ...state, orders: true, detail: false };
    case 'orderCleared':
      return state.orders ? { ...state, orders: false } : state;
  }
}

/** Opening on an existing selection (a shared link) starts with Orders folded. */
export function initialSections(hasSelection: boolean): BoardSectionsState {
  return hasSelection
    ? boardSectionsReducer(INITIAL_SECTIONS, { type: 'orderSelected' })
    : INITIAL_SECTIONS;
}

/** The reducer action for a selection key change, or null when nothing changed. */
export function selectionChangeAction(
  previousKey: string | null,
  nextKey: string | null,
): BoardSectionsAction | null {
  if (previousKey === nextKey) return null;
  return nextKey === null
    ? { type: 'orderCleared' }
    : { type: 'orderSelected' };
}

/** A panel's size as a share of the group, for a pixel height. */
export function pxToPercent(
  px: number,
  containerPx: number,
  fallback: number,
): number {
  if (containerPx <= 0) return fallback;
  return Math.min(100, Math.ceil((px / containerPx) * 100));
}

export const DEFAULT_ORDERS_SHARE = 38;
const SPLIT_KEY = 'replenishment.buyingBoard.ordersShare';
const MIN_SHARE = 10;
const MAX_SHARE = 90;

/** A stored Orders share (percent of Orders + Detail), or the default. */
export function parseOrdersShare(raw: string | null): number {
  if (raw === null) return DEFAULT_ORDERS_SHARE;
  const value = Number(raw);
  return Number.isFinite(value) && value >= MIN_SHARE && value <= MAX_SHARE
    ? value
    : DEFAULT_ORDERS_SHARE;
}

/**
 * The Orders share of a two-panel layout, or null when either panel is folded
 * (collapsed state is never persisted).
 */
export function ordersShareOf(
  layout: readonly number[],
  collapsedSize: number,
): number | null {
  if (layout.length !== 2) return null;
  const [orders, detail] = layout;
  if (orders <= collapsedSize + 0.5 || detail <= collapsedSize + 0.5) {
    return null;
  }
  const share = (orders / (orders + detail)) * 100;
  return share >= MIN_SHARE && share <= MAX_SHARE ? share : null;
}

export function loadOrdersShare(): number {
  try {
    return parseOrdersShare(localStorage.getItem(SPLIT_KEY));
  } catch {
    return DEFAULT_ORDERS_SHARE;
  }
}

export function saveOrdersShare(share: number): void {
  try {
    localStorage.setItem(SPLIT_KEY, String(share));
  } catch {
    // Storage unavailable: the split just does not persist.
  }
}

/**
 * Starting sizes (percent) for the Orders and Detail panels, so a freshly
 * mounted panel set honours the reducer's collapsed flags. A lone Orders panel
 * fills the group; with Detail, a folded side takes `collapsedSize`.
 */
export function panelSizes(
  state: BoardSectionsState,
  hasDetail: boolean,
  collapsedSize: number,
  ordersShare: number = DEFAULT_ORDERS_SHARE,
  ordersCollapsedSize: number = collapsedSize,
): { orders: number; detail: number } {
  if (!hasDetail) return { orders: 100, detail: 0 };
  if (state.orders) {
    return { orders: ordersCollapsedSize, detail: 100 - ordersCollapsedSize };
  }
  if (state.detail) {
    return { orders: 100 - collapsedSize, detail: collapsedSize };
  }
  return { orders: ordersShare, detail: 100 - ordersShare };
}

/**
 * The Line section's share of the group when it opens: enough for the
 * condensed line facts (GEN-2944), leaving the cart's lines most of the room.
 */
export const LINE_SECTION_SHARE = 25;

/** Starting size (percent) of the Line section: 0 when it is not shown. */
export function lineSectionSize(
  state: BoardSectionsState,
  hasLine: boolean,
  collapsedSize: number,
): number {
  if (!hasLine) return 0;
  return state.line ? collapsedSize : LINE_SECTION_SHARE;
}

/** Orders and Detail split what the Line section leaves. */
export function scalePairSizes(
  sizes: { orders: number; detail: number },
  lineSize: number,
): { orders: number; detail: number } {
  const scale = (100 - lineSize) / 100;
  return { orders: sizes.orders * scale, detail: sizes.detail * scale };
}

/** Which panels the group holds; it remounts when this changes. */
export function panelSetOf(hasDetail: boolean, hasLine: boolean): string {
  if (hasLine) return 'orders,detail,line';
  return hasDetail ? 'orders,detail' : 'orders';
}
